import React, { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  Link as LinkIcon,
  WarningAmber as WarningIcon,
  Lock as LockIcon,
  DriveFileRenameOutline as RenameIcon,
  DeleteOutline as DeleteIcon,
  VisibilityOutlined as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useLocation, useSearchParams } from 'react-router-dom';
import cfosService from '../../services/cfosService';
import DetailsPanel from './DetailsPanel';
import FileTypeIcon from './FileTypeIcon';
import StorageMeter from './StorageMeter';
import { collectDroppedFiles } from '../../lib/cfos/droppedFiles';
import { isOfficeWebEditableSize, isOfficeWebFile, isPdfFile } from '../../lib/cfos/fileKind';
import { uniqueTags } from '../../lib/cfos/tags';
import { buildCfosFileLink, copyTextToClipboard } from '../../lib/cfos/deepLink';
import {
  breadcrumbSegments,
  buildFolderTree,
  createVirtualFolder,
  decodeFileDrag,
  encodeFileDrag,
  FILE_DRAG_TYPE,
  folderNameFromPath,
  foldersAtPath,
  joinVirtualPathTree,
  knownFolderPaths,
  loadExtraFolders,
  normalizeVirtualPath,
  pathsEqual,
  remapDescendantPath,
  renameVirtualFolder,
  saveExtraFolders,
  EXTRA_FOLDERS_STORAGE_KEY,
  type FileDragData,
  type FolderNode,
} from '../../lib/cfos/virtualFolders';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../../hooks/useFormFieldErrors';
import FormValidationAlert from '../FormValidationAlert';
import type { FileRecord, StorageUsage, UploadBatchProgress, User } from '../../lib/cfos/types';
import { useSearchParamState } from '../../hooks/useSearchParamState';

export interface BrowseTabProps {
  user: User;
  refreshKey?: number;
  sandboxId: string;
  allowLegacyFolderImport?: boolean;
  /** Sync current folder / deep-link file to URL search params (default false). */
  syncToUrl?: boolean;
  onOpenViewer: (file: FileRecord) => void;
  onOpenOffice: (file: FileRecord, opts?: { preview?: boolean; edit?: boolean }) => void;
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  panelBorder: string;
}

function formatSize(value: number) {
  return value < 1024 * 1024
    ? `${Math.max(1, Math.round(value / 1024))} KB`
    : `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatPerson(user?: { name: string; last_name: string } | null) {
  if (!user) return '—';
  return `${user.name} ${user.last_name}`.trim() || '—';
}

async function downloadFileRecord(file: FileRecord) {
  const blob = await cfosService.fileContentBlob(file.id);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

type FolderJobRole = { id: number; name: string };

type FolderDialog =
  | { kind: 'rename'; path: string; name: string }
  | { kind: 'visibility'; path: string; roles: FolderJobRole[] }
  | { kind: 'remove'; path: string };

function FolderBranch({
  node,
  depth,
  selectedPath,
  expanded,
  dropTarget,
  onPath,
  onToggle,
  onDropFile,
  onFolderMenu,
  setDropTarget,
  panelBorder,
}: {
  node: FolderNode;
  depth: number;
  selectedPath: string;
  expanded: Set<string>;
  dropTarget: string;
  onPath: (path: string) => void;
  onToggle: (path: string) => void;
  onDropFile: (data: FileDragData, destinationPath: string) => void;
  onFolderMenu: (event: MouseEvent<HTMLElement>, path: string) => void;
  setDropTarget: (path: string) => void;
  panelBorder: string;
}) {
  const open = expanded.has(node.path);
  const hasChildren = node.children.length > 0;

  const receiveDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const data = decodeFileDrag(event.dataTransfer.getData(FILE_DRAG_TYPE));
    setDropTarget('');
    if (data?.some((item) => !pathsEqual(item.sourcePath, node.path))) onDropFile(data, node.path);
  };

  return (
    <Box>
      <Box
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes(FILE_DRAG_TYPE)) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'move';
          setDropTarget(node.path);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropTarget('');
        }}
        onDrop={receiveDrop}
        onContextMenu={(event) => onFolderMenu(event, node.path)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: depth * 1.5,
          borderRadius: 1,
          bgcolor: dropTarget === node.path ? 'action.selected' : pathsEqual(selectedPath, node.path) ? 'action.hover' : 'transparent',
          userSelect: 'none',
        }}
      >
        <IconButton size="small" onClick={() => hasChildren && onToggle(node.path)} disabled={!hasChildren} sx={{ width: 28, height: 28 }}>
          {hasChildren ? (open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />) : <Box sx={{ width: 16 }} />}
        </IconButton>
        <Button
          size="small"
          startIcon={pathsEqual(selectedPath, node.path) ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
          onClick={() => onPath(node.path)}
          onContextMenu={(event) => onFolderMenu(event, node.path)}
          sx={{ flex: 1, justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary', minWidth: 0 }}
        >
          <Typography variant="body2" noWrap>{node.name}</Typography>
        </Button>
      </Box>
      {hasChildren && open && node.children.map((child) => (
        <FolderBranch
          key={child.path}
          node={child}
          depth={depth + 1}
          selectedPath={selectedPath}
          expanded={expanded}
          dropTarget={dropTarget}
          onPath={onPath}
          onToggle={onToggle}
          onDropFile={onDropFile}
          onFolderMenu={onFolderMenu}
          setDropTarget={setDropTarget}
          panelBorder={panelBorder}
        />
      ))}
    </Box>
  );
}

const BrowseTab: React.FC<BrowseTabProps> = ({
  user,
  refreshKey = 0,
  sandboxId,
  allowLegacyFolderImport = false,
  syncToUrl = false,
  onOpenViewer,
  onOpenOffice,
  notify,
  panelBorder,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [folderParam, setFolderParam] = useSearchParamState('folder');
  const [fileParam, setFileParam] = useSearchParamState('file');
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [catalogTags, setCatalogTags] = useState<string[]>([]);
  const [localPath, setLocalPath] = useState('/');
  const path = syncToUrl ? normalizeVirtualPath(folderParam ?? '/') : localPath;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);
  const [detailFile, setDetailFile] = useState<FileRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [extraFolders, setExtraFolders] = useState<string[]>([]);
  const [folderRoles, setFolderRoles] = useState<Record<string, number[]>>({});
  const [canCreateFolders, setCanCreateFolders] = useState(true);
  const [jobRoles, setJobRoles] = useState<FolderJobRole[]>([]);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const fieldErrors = useFormFieldErrors();
  const [newFolderRoles, setNewFolderRoles] = useState<FolderJobRole[]>([]);
  const [folderMenu, setFolderMenu] = useState<{ path: string; mouseX: number; mouseY: number } | null>(null);
  const [fileMenu, setFileMenu] = useState<{ file: FileRecord; mouseX: number; mouseY: number } | null>(null);
  const [folderDialog, setFolderDialog] = useState<FolderDialog | null>(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderDropTarget, setFolderDropTarget] = useState('');
  const [fileDropActive, setFileDropActive] = useState(false);
  const [uploadingDrop, setUploadingDrop] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadBatchProgress | null>(null);
  const [linkingSameFiles, setLinkingSameFiles] = useState(false);
  const [storage, setStorage] = useState<StorageUsage | null | undefined>(undefined);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const fileDropDepth = useRef(0);
  const migratedLocalFolders = useRef(false);
  const handledFileDeepLink = useRef<string | null>(null);
  const unknownFolderWarned = useRef<string | null>(null);
  const deepLinkRowRef = useRef<HTMLTableRowElement | null>(null);
  const prevSandboxId = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!sandboxId) return;
    setLoading(true);
    setError('');
    Promise.all([
      cfosService.files({ sandboxId }),
      cfosService.listFolders(sandboxId),
    ])
      .then(async ([fileRows, folderResult]) => {
        const folderPaths = folderResult.paths;
        setCanCreateFolders(folderResult.can_create_folders);
        if (folderResult.roles?.length) setJobRoles(folderResult.roles);
        setFolderRoles(Object.fromEntries(
          (folderResult.folders || []).map((item) => [item.path, item.role_ids]),
        ));
        // One-time import of legacy browser-local empty folders into Shared sandbox only.
        if (!migratedLocalFolders.current) {
          migratedLocalFolders.current = true;
          const legacy = allowLegacyFolderImport ? loadExtraFolders() : [];
          if (legacy.length) {
            try {
              if (!folderResult.can_create_folders) {
                setExtraFolders(folderPaths);
              } else {
                const merged = await cfosService.createFolders(sandboxId, legacy);
                setExtraFolders(merged);
                saveExtraFolders([]);
                localStorage.removeItem(EXTRA_FOLDERS_STORAGE_KEY);
              }
            } catch {
              setExtraFolders(folderPaths);
            }
          } else {
            setExtraFolders(folderPaths);
          }
        } else {
          setExtraFolders(folderPaths);
        }
        setFiles(fileRows);
      })
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setLoading(false));
    cfosService.listTags(undefined, sandboxId).then(setCatalogTags).catch(() => setCatalogTags([]));
    cfosService.storage().then(setStorage).catch(() => setStorage(null));
  }, [sandboxId, allowLegacyFolderImport]);

  useEffect(load, [load]);

  useEffect(() => {
    if (refreshKey > 0) load();
  }, [refreshKey, load]);

  useEffect(() => {
    const sandboxChanged = prevSandboxId.current !== null && prevSandboxId.current !== sandboxId;
    prevSandboxId.current = sandboxId;

    setLocalPath('/');
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
    setDetailFile(null);
    setFolderMenu(null);
    setFileMenu(null);
    setFolderDialog(null);
    migratedLocalFolders.current = false;
    handledFileDeepLink.current = null;
    unknownFolderWarned.current = null;

    // Only wipe deep-link params when the sandbox actually changes (e.g. /cfos dropdown),
    // not on first mount — otherwise pasted links lose folder/file before they open.
    if (sandboxChanged && syncToUrl) {
      setSearchParams(
        (prev) => {
          if (!prev.has('folder') && !prev.has('file')) return prev;
          const next = new URLSearchParams(prev);
          next.delete('folder');
          next.delete('file');
          return next;
        },
        { replace: true },
      );
    }
  }, [sandboxId, syncToUrl, setSearchParams]);

  useEffect(() => {
    if (path === '/') return;
    const parts = path.split('/').filter(Boolean);
    setExpanded((current) => {
      const next = new Set(current);
      for (let index = 1; index <= parts.length; index += 1) {
        next.add(`/${parts.slice(0, index).join('/')}`);
      }
      return next;
    });
  }, [path]);

  const knownTags = useMemo(() => uniqueTags([{ tags: catalogTags }, ...files]), [catalogTags, files]);
  const tree = useMemo(
    () => buildFolderTree([...files.flatMap((file) => file.paths.map((item) => item.path)), ...extraFolders]),
    [files, extraFolders],
  );
  const knownFolders = useMemo(
    () => knownFolderPaths([...files.flatMap((file) => file.paths.map((item) => item.path)), ...extraFolders]),
    [files, extraFolders],
  );
  const childFolders = useMemo(() => foldersAtPath(tree, path), [tree, path]);
  const visible = useMemo(
    () => files.filter((file) => file.paths.some((item) => pathsEqual(item.path, path))),
    [files, path],
  );
  const detailRecord = detailFile ? files.find((f) => f.id === detailFile.id) || detailFile : null;
  const selectedArePdfs = selectedIds.size > 1 && [...selectedIds].every((id) => {
    const record = files.find((item) => item.id === id);
    return record ? isPdfFile(record) : false;
  });

  // Unknown / inaccessible folder in the URL → fall back to root.
  useEffect(() => {
    if (!syncToUrl || loading || path === '/') return;
    if (knownFolders.has(path)) {
      unknownFolderWarned.current = null;
      return;
    }
    if (unknownFolderWarned.current === path) return;
    unknownFolderWarned.current = path;
    notify(`Folder ${path} was not found or is not accessible.`, 'warning');
    setFolderParam(null, { replace: true });
  }, [syncToUrl, loading, path, knownFolders, notify, setFolderParam]);

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
    setDetailFile(null);
  };

  const setPath = useCallback(
    (next: string, opts?: { replace?: boolean; clearFile?: boolean }) => {
      const normalized = normalizeVirtualPath(next);
      if (syncToUrl) {
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev);
            if (normalized === '/') params.delete('folder');
            else params.set('folder', normalized);
            if (opts?.clearFile !== false) params.delete('file');
            return params;
          },
          { replace: opts?.replace ?? false },
        );
      } else {
        setLocalPath(normalized);
      }
    },
    [syncToUrl, setSearchParams],
  );

  const goToPath = (next: string) => {
    setPath(next);
    clearSelection();
  };

  const openFile = (file: FileRecord) => {
    if (isPdfFile(file)) {
      onOpenViewer(file);
      return;
    }
    if (isOfficeWebFile(file) && isOfficeWebEditableSize(file)) {
      onOpenOffice(file);
      return;
    }
    if (isOfficeWebFile(file) && !isOfficeWebEditableSize(file)) {
      notify('File exceeds Microsoft 365 web edit size limit — downloading instead', 'warning');
    }
    void downloadFileRecord(file).catch((err) => notify((err as Error).message, 'error'));
  };

  const selectFile = (file: FileRecord, event: MouseEvent) => {
    const index = visible.findIndex((item) => item.id === file.id);
    if (event.shiftKey && selectionAnchorId) {
      const anchorIndex = visible.findIndex((item) => item.id === selectionAnchorId);
      if (anchorIndex >= 0 && index >= 0) {
        const start = Math.min(anchorIndex, index);
        const end = Math.max(anchorIndex, index);
        setSelectedIds(new Set(visible.slice(start, end + 1).map((item) => item.id)));
        setDetailFile(file);
        return;
      }
    }
    if (event.metaKey || event.ctrlKey) {
      setSelectedIds((current) => {
        const next = new Set(current);
        if (next.has(file.id)) next.delete(file.id);
        else next.add(file.id);
        return next;
      });
      setSelectionAnchorId(file.id);
      setDetailFile(file);
      return;
    }
    setSelectedIds(new Set([file.id]));
    setSelectionAnchorId(file.id);
    setDetailFile(file);
  };

  const getDragDataFor = (fileId: string): FileDragData | undefined => {
    const ids = selectedIds.has(fileId) && selectedIds.size > 1 ? [...selectedIds] : [fileId];
    const items = ids.flatMap((id) => {
      const file = visible.find((item) => item.id === id);
      const location = file?.paths.find((item) => pathsEqual(item.path, path));
      return location ? [{ fileId: id, pathId: location.id, sourcePath: normalizeVirtualPath(location.path) }] : [];
    });
    return items.length ? items : undefined;
  };

  const moveFiles = async (data: FileDragData, destinationPath: string) => {
    const destination = normalizeVirtualPath(destinationPath);
    const toMove = data.filter((item) => !pathsEqual(item.sourcePath, destination));
    if (!toMove.length) return;
    const results = await Promise.allSettled(toMove.map((item) => cfosService.movePath(item.fileId, item.pathId, destination)));
    const moved = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected');
    clearSelection();
    load();
    if (moved && !failed.length) {
      notify(`${moved} file${moved === 1 ? '' : 's'} moved to ${destination}.`, 'success');
      return;
    }
    if (moved && failed.length) {
      notify(`${moved} moved, ${failed.length} failed.`, 'error');
      return;
    }
    const firstError = failed[0]?.status === 'rejected' ? (failed[0] as PromiseRejectedResult).reason : null;
    notify(firstError instanceof Error ? firstError.message : 'Could not move files.', 'error');
  };

  const linkSelectedAsSameFiles = async () => {
    if (selectedIds.size < 2 || linkingSameFiles) return;
    const ids = [...selectedIds];
    setLinkingSameFiles(true);
    try {
      await cfosService.linkSameFiles(ids);
      notify(`${ids.length} files marked as the same document.`, 'success');
      clearSelection();
      load();
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setLinkingSameFiles(false);
    }
  };

  const knownFolderSources = [
    ...files.flatMap((file) => file.paths.map((item) => item.path)),
    ...extraFolders,
  ];

  const closeFolderMenu = () => setFolderMenu(null);

  const openFolderMenu = (event: MouseEvent<HTMLElement>, folderPath: string) => {
    event.preventDefault();
    event.stopPropagation();
    if (folderPath === '/') return;
    setFileMenu(null);
    setFolderMenu({
      path: folderPath,
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const closeFileMenu = () => setFileMenu(null);

  const openFileMenu = (event: MouseEvent<HTMLElement>, file: FileRecord) => {
    event.preventDefault();
    event.stopPropagation();
    setFolderMenu(null);
    setFileMenu({
      file,
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const copyFileLink = async (file: FileRecord) => {
    closeFileMenu();
    try {
      const link = buildCfosFileLink({
        origin: window.location.origin,
        pathname: location.pathname,
        search: location.search,
        folder: path,
        fileId: file.id,
      });
      await copyTextToClipboard(link);
      notify('Link copied to clipboard.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not copy link.', 'error');
    }
  };

  const revealAndSelectFile = useCallback((file: FileRecord, folder: string) => {
    const targetFolder = normalizeVirtualPath(folder);
    if (!pathsEqual(path, targetFolder)) {
      setPath(targetFolder, { clearFile: false, replace: true });
    }
    setSelectedIds(new Set([file.id]));
    setSelectionAnchorId(file.id);
    setDetailFile(file);
  }, [path, setPath]);

  const openDeepLinkedFile = useCallback((file: FileRecord) => {
    if (isPdfFile(file)) {
      onOpenViewer(file);
      return;
    }
    if (isOfficeWebFile(file) && isOfficeWebEditableSize(file)) {
      onOpenOffice(file);
      return;
    }
    // Non-previewable: reveal only — never auto-download from a shared link.
  }, [onOpenViewer, onOpenOffice]);

  // Auto-open / reveal from ?file= deep links.
  useEffect(() => {
    if (!syncToUrl || loading || !fileParam) return;
    if (handledFileDeepLink.current === fileParam) return;

    let cancelled = false;

    const run = async () => {
      let record = files.find((item) => item.id === fileParam) || null;
      if (!record) {
        try {
          record = await cfosService.file(fileParam);
        } catch {
          if (!cancelled) {
            handledFileDeepLink.current = fileParam;
            notify('File was not found or you do not have access.', 'warning');
            setFileParam(null, { replace: true });
          }
          return;
        }
      }
      if (cancelled || !record) return;

      const preferredFolder = folderParam
        ? normalizeVirtualPath(folderParam)
        : normalizeVirtualPath(record.paths[0]?.path || '/');
      const matchingPath = record.paths.find((item) => pathsEqual(item.path, preferredFolder));
      const targetFolder = matchingPath
        ? normalizeVirtualPath(matchingPath.path)
        : normalizeVirtualPath(record.paths[0]?.path || '/');

      handledFileDeepLink.current = fileParam;
      setFiles((prev) => (prev.some((item) => item.id === record!.id) ? prev : [...prev, record!]));
      revealAndSelectFile(record, targetFolder);
      openDeepLinkedFile(record);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    syncToUrl,
    loading,
    fileParam,
    folderParam,
    files,
    notify,
    setFileParam,
    revealAndSelectFile,
    openDeepLinkedFile,
  ]);

  // Scroll the deep-linked row into view after the folder contents render.
  useEffect(() => {
    if (!syncToUrl || !fileParam || handledFileDeepLink.current !== fileParam) return;
    if (!visible.some((item) => item.id === fileParam)) return;
    const frame = requestAnimationFrame(() => {
      deepLinkRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [syncToUrl, fileParam, path, visible]);

  const rolesForFolder = (folderPath: string): FolderJobRole[] => {
    const ids = folderRoles[folderPath] || [];
    return jobRoles.filter((role) => ids.includes(role.id));
  };

  const openRenameDialog = (folderPath: string) => {
    closeFolderMenu();
    setFolderDialog({ kind: 'rename', path: folderPath, name: folderNameFromPath(folderPath) });
  };

  const openVisibilityDialog = (folderPath: string) => {
    closeFolderMenu();
    setFolderDialog({ kind: 'visibility', path: folderPath, roles: rolesForFolder(folderPath) });
  };

  const openRemoveDialog = (folderPath: string) => {
    closeFolderMenu();
    setFolderDialog({ kind: 'remove', path: folderPath });
  };

  const persistRenamedFolder = async () => {
    if (!folderDialog || folderDialog.kind !== 'rename' || folderBusy) return;
    if (!canCreateFolders) {
      notify('You are not allowed to rename folders here.', 'error');
      return;
    }
    const result = renameVirtualFolder(folderDialog.path, folderDialog.name, knownFolderSources);
    if ('error' in result) {
      notify(result.error, 'error');
      return;
    }
    setFolderBusy(true);
    try {
      const stored = await cfosService.renameFolder(sandboxId, folderDialog.path, folderDialog.name);
      setExtraFolders(stored.paths);
      setFolderRoles(Object.fromEntries(stored.folders.map((item) => [item.path, item.role_ids])));
      const from = folderDialog.path;
      const to = stored.path;
      setPath(remapDescendantPath(path, from, to), { replace: true, clearFile: false });
      setExpanded((current) => {
        const next = new Set<string>();
        current.forEach((item) => next.add(remapDescendantPath(item, from, to)));
        return next;
      });
      setFolderDialog(null);
      notify(`Folder renamed to ${folderNameFromPath(to)}.`, 'success');
      load();
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setFolderBusy(false);
    }
  };

  const persistFolderVisibility = async () => {
    if (!folderDialog || folderDialog.kind !== 'visibility' || folderBusy) return;
    if (!canCreateFolders) {
      notify('You are not allowed to edit folder visibility here.', 'error');
      return;
    }
    setFolderBusy(true);
    try {
      const stored = await cfosService.updateFolderVisibility(
        sandboxId,
        folderDialog.path,
        folderDialog.roles.map((role) => role.id),
      );
      setExtraFolders(stored.paths);
      setFolderRoles(Object.fromEntries(stored.folders.map((item) => [item.path, item.role_ids])));
      setFolderDialog(null);
      notify('Folder visibility updated.', 'success');
      load();
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setFolderBusy(false);
    }
  };

  const persistRemovedFolder = async () => {
    if (!folderDialog || folderDialog.kind !== 'remove' || folderBusy) return;
    if (!canCreateFolders) {
      notify('You are not allowed to remove folders here.', 'error');
      return;
    }
    setFolderBusy(true);
    try {
      const removedPath = folderDialog.path;
      await cfosService.deleteFolder(sandboxId, removedPath);
      if (path === removedPath || path.startsWith(`${removedPath}/`)) {
        goToPath('/');
      }
      setFolderDialog(null);
      notify(`Folder ${folderNameFromPath(removedPath)} removed.`, 'success');
      load();
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setFolderBusy(false);
    }
  };

  const persistCreatedFolder = async () => {
    if (!newFolderName.trim()) {
      fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
      return;
    }
    if (!canCreateFolders) {
      notify('You are not allowed to create folders here.', 'error');
      return;
    }
    const result = createVirtualFolder(path, newFolderName, [
      ...files.flatMap((file) => file.paths.map((item) => item.path)),
      ...extraFolders,
    ]);
    if ('error' in result) {
      notify(result.error, 'error');
      return;
    }
    try {
      const stored = await cfosService.createFolders(
        sandboxId,
        [result.path],
        newFolderRoles.map((role) => role.id),
      );
      setExtraFolders(stored);
      setCreatingFolder(false);
      setNewFolderName('');
      setNewFolderRoles([]);
      goToPath(result.path);
      notify(`Folder ${result.path} created.`, 'success');
    } catch (err) {
      notify((err as Error).message, 'error');
    }
  };

  const isOsFileDrag = (event: DragEvent) => {
    const types = Array.from(event.dataTransfer.types);
    return types.includes('Files') && !types.includes(FILE_DRAG_TYPE);
  };

  const uploadDroppedFiles = async (dataTransfer: DataTransfer) => {
    const destination = normalizeVirtualPath(path);
    const treeResult = await collectDroppedFiles(dataTransfer);
    if (treeResult.emptyRelativeDirs.length) {
      const emptyPaths = treeResult.emptyRelativeDirs
        .map((relative) => joinVirtualPathTree(destination, relative))
        .filter((value): value is string => Boolean(value) && value !== '/');
      if (emptyPaths.length) {
        if (!canCreateFolders) {
          notify('You are not allowed to create folders here.', 'error');
        } else {
          try {
            const stored = await cfosService.createFolders(sandboxId, emptyPaths);
            setExtraFolders(stored);
          } catch {
            /* ignore empty-folder failures during drop upload */
          }
        }
      }
    }
    if (!treeResult.items.length) {
      notify('No files found to upload.', 'warning');
      return;
    }
    setUploadingDrop(true);
    setUploadProgress({ completed: 0, total: treeResult.items.length, batchIndex: 0, batchCount: 0 });
    try {
      const items = treeResult.items.map((item) => ({
        file: item.file,
        path: item.relativeDir
          ? joinVirtualPathTree(destination, item.relativeDir) || destination
          : destination,
      }));
      const result = await cfosService.uploadInBatches(items, sandboxId, {
        onProgress: setUploadProgress,
      });
      load();
      const total = result.uploaded.length + result.linked.length;
      notify(total ? `${total} file(s) uploaded to ${destination}.` : 'Upload completed with warnings.', total ? 'success' : 'warning');
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setUploadingDrop(false);
      setUploadProgress(null);
    }
  };

  const toggleFolder = (folderPath: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(folderPath)) next.delete(folderPath);
      else next.add(folderPath);
      return next;
    });
  };

  const segments = breadcrumbSegments(path);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 180px)', minHeight: 400, border: 1, borderColor: panelBorder, borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ width: 220, flexShrink: 0, borderRight: 1, borderColor: panelBorder, overflow: 'auto', p: 1, display: { xs: 'none', md: 'block' } }}>
        <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>Folders</Typography>
        <Button
          fullWidth
          size="small"
          startIcon={<FolderOpenIcon fontSize="small" />}
          onClick={() => goToPath('/')}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', mt: 0.5 }}
          variant={path === '/' ? 'contained' : 'text'}
        >
          Root
        </Button>
        {tree.map((node) => (
          <FolderBranch
            key={node.path}
            node={node}
            depth={0}
            selectedPath={path}
            expanded={expanded}
            dropTarget={folderDropTarget}
            onPath={goToPath}
            onToggle={toggleFolder}
            onDropFile={moveFiles}
            onFolderMenu={openFolderMenu}
            setDropTarget={setFolderDropTarget}
            panelBorder={panelBorder}
          />
        ))}
        <Box sx={{ mt: 2, px: 1 }}>
          <StorageMeter usage={storage} compact />
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ p: 1, borderBottom: 1, borderColor: panelBorder, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ flex: 1 }}>
            <Link component="button" variant="body2" underline="hover" onClick={() => goToPath('/')}>Root</Link>
            {segments.map((seg) => (
              <Link
                key={seg.path}
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => goToPath(seg.path)}
                onContextMenu={(event) => openFolderMenu(event, seg.path)}
              >
                {seg.name}
              </Link>
            ))}
          </Breadcrumbs>
          {canCreateFolders && (
            <Button size="small" variant="outlined" onClick={() => setCreatingFolder(true)}>New folder</Button>
          )}
          {selectedArePdfs && (
            <Button size="small" variant="outlined" disabled={linkingSameFiles} onClick={() => void linkSelectedAsSameFiles()}>
              Link as same file
            </Button>
          )}
        </Box>

        <Box
          sx={{ flex: 1, overflow: 'auto', position: 'relative' }}
          onDragEnter={(e) => {
            if (isOsFileDrag(e)) { fileDropDepth.current += 1; setFileDropActive(true); }
          }}
          onDragLeave={() => {
            fileDropDepth.current -= 1;
            if (fileDropDepth.current <= 0) { fileDropDepth.current = 0; setFileDropActive(false); }
          }}
          onDragOver={(e) => { if (isOsFileDrag(e)) e.preventDefault(); }}
          onDrop={(e) => {
            if (!isOsFileDrag(e)) return;
            e.preventDefault();
            fileDropDepth.current = 0;
            setFileDropActive(false);
            void uploadDroppedFiles(e.dataTransfer);
          }}
        >
          {fileDropActive && (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(204,186,155,0.15)', zIndex: 2, display: 'grid', placeItems: 'center', border: 2, borderStyle: 'dashed', borderColor: '#ccba9b' }}>
              <Typography variant="body1" fontWeight={600}>Drop files to upload here</Typography>
            </Box>
          )}
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>}
          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
          {!loading && !error && (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Last modification</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {childFolders.map((folder) => (
                  <TableRow
                    key={folder.path}
                    hover
                    onDoubleClick={() => goToPath(folder.path)}
                    onContextMenu={(event) => openFolderMenu(event, folder.path)}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FolderIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={600}>{folder.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                  </TableRow>
                ))}
                {visible.map((file) => {
                  const selected = selectedIds.has(file.id);
                  const dragData = getDragDataFor(file.id);
                  const isDeepLinkTarget = syncToUrl && fileParam === file.id;
                  return (
                    <TableRow
                      key={`${file.id}-${path}`}
                      ref={isDeepLinkTarget ? deepLinkRowRef : undefined}
                      hover
                      selected={selected}
                      draggable={Boolean(dragData?.length)}
                      onDragStart={(e) => {
                        if (!dragData?.length) { e.preventDefault(); return; }
                        e.dataTransfer.setData(FILE_DRAG_TYPE, encodeFileDrag(dragData));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={(e) => selectFile(file, e)}
                      onDoubleClick={() => openFile(file)}
                      onContextMenu={(event) => openFileMenu(event, file)}
                      sx={{ cursor: dragData?.length ? 'grab' : 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                          <FileTypeIcon filename={file.filename} />
                          <Typography variant="body2" fontWeight={600} noWrap>{file.filename}</Typography>
                          {file.is_contractual && <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                          {file.same_file_group_id && <LinkIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                          {file.contractual_orphan && <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                          {file.office_session && (
                            <LockIcon
                              sx={{ fontSize: 16, color: 'warning.main' }}
                              titleAccess={
                                file.office_session.status === 'checked_out'
                                  ? 'Checked out in Microsoft 365'
                                  : 'Microsoft 365 preview open'
                              }
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{formatPerson(file.uploader)}</Typography>
                      </TableCell>
                      <TableCell>{formatSize(file.file_size)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {formatPerson(file.last_modified_by || file.uploader)}
                        </Typography>
                      </TableCell>
                      <TableCell>{new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(file.updated_at))}</TableCell>
                    </TableRow>
                  );
                })}
                {!childFolders.length && !visible.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      This folder is empty. Drop files here or create a subfolder.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          {uploadingDrop && uploadProgress && (
            <Typography variant="caption" sx={{ p: 1, display: 'block' }}>
              Uploading {uploadProgress.completed} / {uploadProgress.total}…
            </Typography>
          )}
        </Box>
      </Box>

      {detailRecord && (
        <DetailsPanel
          file={detailRecord}
          files={files}
          selectedIds={selectedIds}
          knownTags={knownTags}
          user={user}
          sandboxId={sandboxId}
          onClose={clearSelection}
          refresh={load}
          notify={notify}
          panelBorder={panelBorder}
          onOpenOffice={onOpenOffice}
          onFileReplaced={(next) => {
            setDetailFile(next);
            void load();
          }}
        />
      )}

      <Dialog
        open={creatingFolder}
        onClose={() => {
          setCreatingFolder(false);
          setNewFolderName('');
          setNewFolderRoles([]);
          fieldErrors.clear();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create folder</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder name"
            value={newFolderName}
            error={fieldErrors.invalid('name')}
            onChange={(e) => {
              fieldErrors.clearField('name');
              setNewFolderName(e.target.value);
            }}
            sx={{ mt: 1 }}
          />
          <Autocomplete
            multiple
            size="small"
            options={jobRoles}
            getOptionLabel={(option) => option.name}
            value={newFolderRoles}
            onChange={(_, value) => setNewFolderRoles(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Visible to"
                placeholder={newFolderRoles.length ? undefined : 'All roles'}
              />
            )}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Leave empty to allow all CFOS users. Nested folders stay hidden unless the user can also access every parent.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreatingFolder(false);
              setNewFolderName('');
              setNewFolderRoles([]);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={persistCreatedFolder}>Create</Button>
        </DialogActions>
      </Dialog>

      <Menu
        open={Boolean(folderMenu)}
        onClose={closeFolderMenu}
        anchorReference="anchorPosition"
        anchorPosition={folderMenu ? { top: folderMenu.mouseY, left: folderMenu.mouseX } : undefined}
        slotProps={{
          paper: {
            sx: {
              minWidth: 196,
              border: 1,
              borderColor: panelBorder,
              borderRadius: 1,
            },
          },
        }}
      >
        <MenuItem
          disabled={!canCreateFolders}
          onClick={() => folderMenu && openRenameDialog(folderMenu.path)}
        >
          <ListItemIcon><RenameIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!canCreateFolders}
          onClick={() => folderMenu && openVisibilityDialog(folderMenu.path)}
        >
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit visibility</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!canCreateFolders}
          onClick={() => folderMenu && openRemoveDialog(folderMenu.path)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        open={Boolean(fileMenu)}
        onClose={closeFileMenu}
        anchorReference="anchorPosition"
        anchorPosition={fileMenu ? { top: fileMenu.mouseY, left: fileMenu.mouseX } : undefined}
        slotProps={{
          paper: {
            sx: {
              minWidth: 180,
              border: 1,
              borderColor: panelBorder,
              borderRadius: 1,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (fileMenu) void copyFileLink(fileMenu.file);
          }}
        >
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={folderDialog?.kind === 'rename'}
        onClose={() => !folderBusy && setFolderDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder name"
            value={folderDialog?.kind === 'rename' ? folderDialog.name : ''}
            onChange={(event) => {
              setFolderDialog((current) => (
                current?.kind === 'rename' ? { ...current, name: event.target.value } : current
              ));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void persistRenamedFolder();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={folderBusy} onClick={() => setFolderDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={folderBusy || folderDialog?.kind !== 'rename' || !folderDialog.name.trim()}
            onClick={() => void persistRenamedFolder()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={folderDialog?.kind === 'remove'}
        onClose={() => !folderBusy && setFolderDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove folder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Remove {folderDialog?.kind === 'remove' ? folderNameFromPath(folderDialog.path) : 'this folder'} and its empty subfolders?
            Move files out first — files are not deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={folderBusy} onClick={() => setFolderDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={folderBusy}
            onClick={() => void persistRemovedFolder()}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={folderDialog?.kind === 'visibility'}
        onClose={() => !folderBusy && setFolderDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit visibility</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            size="small"
            options={jobRoles}
            getOptionLabel={(option) => option.name}
            value={folderDialog?.kind === 'visibility' ? folderDialog.roles : []}
            onChange={(_, value) => {
              setFolderDialog((current) => (
                current?.kind === 'visibility' ? { ...current, roles: value } : current
              ));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Visible to"
                placeholder={folderDialog?.kind === 'visibility' && folderDialog.roles.length ? undefined : 'All roles'}
              />
            )}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Leave empty to allow all CFOS users. Nested folders stay hidden unless the user can also access every parent.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={folderBusy} onClick={() => setFolderDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={folderBusy}
            onClick={() => void persistFolderVisibility()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrowseTab;
